import React, { useState, useEffect } from "react";
import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import OfficeFilterByAllActive from "./OfficeFilterByAllActive";
import EmployeesFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/EmployeesFilter";
import { withTranslation } from "react-i18next";
import StatusCheckFilter from "./StatusCheckFilter";
import CategoryFilter from "./CategoryFilter";
import DateRangeFilter from "./DateRangeFilter";
import styles from "../../Notes/Notes.module.scss";
import officePolicystyles from "../OfficePolicyManagement.module.scss";

const COMPLIANCE_DASHBOARD_ACCORDION_KEY = "complianceDashboardAccordion";
const DEFAULT_COMPLIANCE_ACCORDION_EXPANDED = ["date", "a", "b", "cat", "c"];

/**
 * Filters for Compliance Dashboard (Current Policies): Date Range, Offices, Staff, Category, Status.
 */
const ComplianceDashboardFilters = ({
  t,
  isSidebarActive,
  handleSidebarToggle,
  resetFilter,
  officeFilter,
  setOfficeFilter,
  handleApplyFilters,
  staffFilter,
  setStaffFilter,
  statusFilter,
  setStatusFilter,
  categoryFilter,
  setCategoryFilter,
  categoryList,
  dateRange,
  setDateRange,
  isFilterApply,
  isStaff,
  accountOwnerId,
}) => {
  const [localSelectedStaffIds, setLocalSelectedStaffIds] = useState([]);
  const [staffIdToUserIdMap, setStaffIdToUserIdMap] = useState(new Map());
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [expandedAccordionItems, setExpandedAccordionItems] = useState(() => {
    const validUuids = new Set(DEFAULT_COMPLIANCE_ACCORDION_EXPANDED);
    try {
      const saved = sessionStorage.getItem(COMPLIANCE_DASHBOARD_ACCORDION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter((uuid) => validUuids.has(uuid));
          if (filtered.length > 0) {
            return filtered;
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
    return [...DEFAULT_COMPLIANCE_ACCORDION_EXPANDED];
  });

  const handleAccordionChange = (uuids) => {
    setExpandedAccordionItems(uuids);
    try {
      sessionStorage.setItem(
        COMPLIANCE_DASHBOARD_ACCORDION_KEY,
        JSON.stringify(uuids),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (staffFilter?.length === 0) {
      setLocalSelectedStaffIds([]);
    }
  }, [staffFilter]);

  const convertStaffIdsToUserIds = (staffIds) => {
    const arr = Array.isArray(staffIds) ? staffIds : [];
    const userIds = arr
      .map((staffId) => staffIdToUserIdMap.get(staffId))
      .filter(Boolean);
    return [...new Set(userIds)];
  };

  const setUsersListAndMap = (list) => {
    setStaffList(list || []);
    const map = new Map();
    (list || []).forEach((s) => {
      const uid = s?.userId ?? s?.user?.userId ?? s?.user?.id;
      if (s?.id != null && uid != null) map.set(s.id, uid);
    });
    setStaffIdToUserIdMap(map);
  };

  useEffect(() => {
    if (!staffList?.length || !staffFilter?.length) {
      if (!staffFilter?.length) setLocalSelectedStaffIds([]);
      return;
    }
    const staffIds = staffList
      .filter((s) => {
        const uid = s?.userId ?? s?.user?.userId ?? s?.user?.id;
        return uid && staffFilter.includes(uid);
      })
      .map((s) => s.id)
      .filter(Boolean);
    setLocalSelectedStaffIds(staffIds);
  }, [staffFilter, staffList]);

  const handleApplyFilter = () => {
    handleApplyFilters();
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  };

  const handleResetFilter = () => {
    setLocalSelectedStaffIds([]);
    setSelectedRoles([]);
    setResetSearchTrigger((prev) => prev + 1);
    resetFilter();
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  };

  const handleClose = () => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  };

  return (
    <StickySidebar
      isSidebarActive={isSidebarActive}
      handleSidebarToggle={handleSidebarToggle}
      resetFilter={handleResetFilter}
    >
      <span className={styles.notes_filter}>
        {t("taskManagement.filters")}
        {isFilterApply && <span className={styles.red_dot}></span>}
      </span>
      <Accordion
        preExpanded={expandedAccordionItems}
        className="filter-accordion compliance-dashboard-accordion"
        allowZeroExpanded
        allowMultipleExpanded
        onChange={handleAccordionChange}
      >
        <AccordionItem uuid="date">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("accountOwner.dateRange")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <DateRangeFilter
              dateRange={dateRange}
              setDateRange={setDateRange}
              t={t}
            />
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="a" className="border-0 m-0 p-0">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("taskManagement.offices")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <OfficeFilterByAllActive
              selectedOffice={officeFilter}
              setOfficeFilter={setOfficeFilter}
              ownerId={accountOwnerId}
              t={t}
            />
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="b">
          <AccordionItemHeading>
            <AccordionItemButton>
              {isStaff
                ? t("taskManagement.assigonr")
                : t("taskManagement.staffMembers")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel
            className={
              officePolicystyles["staff-filter-panel"] + " accordion__panel"
            }
          >
            <EmployeesFilter
              t={t}
              selectedEmpFilter={localSelectedStaffIds}
              setEmpFilter={(staffIdsOrUpdater) => {
                const nextStaffIds =
                  typeof staffIdsOrUpdater === "function"
                    ? staffIdsOrUpdater(localSelectedStaffIds)
                    : staffIdsOrUpdater;
                const ids = Array.isArray(nextStaffIds) ? nextStaffIds : [];
                setLocalSelectedStaffIds(ids);
                setStaffFilter(convertStaffIdsToUserIds(ids));
              }}
              ownerId={accountOwnerId}
              selectedOfficeFilter={officeFilter}
              selectedRoles={selectedRoles}
              setSelectedRoles={setSelectedRoles}
              ownerData={null}
              hideOwner={true}
              showProfilePic={true}
              deduplicateByUserId={true}
              hideRoleFilter={false}
              resetSearchTrigger={resetSearchTrigger}
              setUsersList={setUsersListAndMap}
            />
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="cat">
          <AccordionItemHeading>
            <AccordionItemButton>
              {t("officePolicyManagement.category")}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <CategoryFilter
              categoryList={categoryList}
              selectedCategory={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              t={t}
            />
          </AccordionItemPanel>
        </AccordionItem>
        <AccordionItem uuid="c">
          <AccordionItemHeading>
            <AccordionItemButton>{t("notes.status")}</AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>
            <StatusCheckFilter
              seletedStatus={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
      <div className="filter-btn-box filter-btn-box--sticky-footer compliance-dashboard-filter-btns">
        <button
          className="button button-round button-shadow mr-3 compliance-apply-btn"
          title="apply"
          onClick={handleApplyFilter}
        >
          {t("taskManagement.apply")}
        </button>
        <button
          className="button button-round button-border button-dark reset-btn compliance-reset-btn"
          title="reset"
          onClick={handleResetFilter}
        >
          {t("taskManagement.reset")}
        </button>
        <button
          className="button button-round button-border button-dark cancel-btn compliance-cancel-btn"
          title="close"
          onClick={handleClose}
        >
          {t("taskManagement.close")}
        </button>
      </div>
    </StickySidebar>
  );
};

export default withTranslation()(ComplianceDashboardFilters);
